import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Package,
  ArrowRight,
  Laptop,
  Users,
  DollarSign,
  Star,
  ExternalLink,
} from "lucide-react";

const softwareItems = [
  {
    name: "Google for Nonprofits",
    category: "Productivity",
    value: "$2,400/year",
    description: "Google Workspace, Google Ad Grants, YouTube for Nonprofits",
    recommended: true,
  },
  {
    name: "Microsoft 365 for Nonprofits",
    category: "Productivity",
    value: "$3,600/year",
    description: "Office 365 E1, Teams, SharePoint, OneDrive",
    recommended: true,
  },
  {
    name: "Salesforce NPSP",
    category: "CRM",
    value: "$18,000/year",
    description: "10 free licenses, donor management, volunteer tracking",
    recommended: false,
  },
  {
    name: "Canva for Nonprofits",
    category: "Design",
    value: "$156/year",
    description: "Full Canva Pro access for entire team",
    recommended: true,
  },
];

const partnerships = [
  {
    type: "Collaboration",
    title: "Nonprofit ↔ Nonprofit",
    description: "Find complementary organizations for joint program delivery and shared services.",
  },
  {
    type: "Research",
    title: "Academic Partnerships",
    description: "Connect with universities for impact evaluation and research collaboration.",
  },
  {
    type: "Co-funding",
    title: "Government Programs",
    description: "Discover municipal and provincial co-funding opportunities.",
  },
];

export const ResourceHubPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 gradient-hero" data-testid="resourcehub-hero">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-6 bg-violet-100 text-violet-700 hover:bg-violet-100 border-0 px-4 py-1.5 text-sm font-medium">
                  <Package className="h-3.5 w-3.5 mr-1.5" />
                  Resources & Partnerships
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-zinc-900 tracking-tight mb-6"
              >
                ResourceHub
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-zinc-600 leading-relaxed mb-8"
              >
                Lower operational costs with free software and strategic partnerships. 
                Access tools worth $25,000+/year and connect with 83,000+ organizations.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-violet-600 hover:bg-violet-700 text-white font-medium px-8"
                    data-testid="resourcehub-cta"
                  >
                    Explore Resources
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="grid grid-cols-3 gap-6 mt-12"
              >
                <div>
                  <p className="text-3xl font-heading font-bold text-zinc-900">500+</p>
                  <p className="text-sm text-zinc-500">Free tools</p>
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold text-zinc-900">$25K+</p>
                  <p className="text-sm text-zinc-500">Avg savings/year</p>
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold text-zinc-900">83K+</p>
                  <p className="text-sm text-zinc-500">Partner network</p>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-zinc-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-zinc-900">Top Recommendations</h3>
                  <Badge className="bg-violet-100 text-violet-700">For you</Badge>
                </div>
                <div className="space-y-3">
                  {softwareItems.slice(0, 3).map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-zinc-50 rounded-xl hover:bg-violet-50 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                          <Laptop className="h-5 w-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900 text-sm">{item.name}</p>
                          <p className="text-xs text-zinc-500">{item.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-emerald-600">{item.value}</p>
                        {item.recommended && (
                          <div className="flex items-center gap-1 text-amber-500">
                            <Star className="h-3 w-3 fill-amber-500" />
                            <span className="text-xs">Top pick</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Software Directory */}
      <section className="py-20 md:py-32 bg-white" data-testid="software-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-violet-100 text-violet-700 hover:bg-violet-100 border-0">
              <Laptop className="h-3 w-3 mr-1" />
              Free Software Directory
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              500+ free tools for nonprofits
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              AI-curated directory of free and discounted software, personalized to your organization's needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {softwareItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100 hover:border-violet-200 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                      <Laptop className="h-6 w-6 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-zinc-900">{item.name}</h3>
                      <Badge variant="secondary" className="text-xs mt-1">{item.category}</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-heading font-bold text-emerald-600">{item.value}</p>
                    <p className="text-xs text-zinc-500">savings</p>
                  </div>
                </div>
                <p className="text-sm text-zinc-600 mb-4">{item.description}</p>
                <Button variant="outline" size="sm" className="w-full">
                  View Details <ExternalLink className="ml-2 h-3 w-3" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem Matchmaker */}
      <section className="py-20 md:py-32 bg-zinc-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-violet-100 text-violet-700 hover:bg-violet-100 border-0">
              <Users className="h-3 w-3 mr-1" />
              Ecosystem Matchmaker
            </Badge>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              Find your perfect partners
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Connect with 83,000+ organizations for collaboration, shared services, and co-funded programs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {partnerships.map((item, index) => (
              <motion.div
                key={item.type}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-zinc-100 hover:shadow-lg transition-all text-center"
              >
                <Badge className="mb-4 bg-violet-100 text-violet-700">{item.type}</Badge>
                <h3 className="text-xl font-heading font-semibold text-zinc-900 mb-3">{item.title}</h3>
                <p className="text-zinc-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-violet-600">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight mb-6">
            Save thousands every year
          </h2>
          <p className="text-lg text-violet-100 mb-10 max-w-2xl mx-auto">
            Access free software and find strategic partners to reduce costs and expand your reach.
          </p>
          <Link to="/dashboard">
            <Button
              size="lg"
              className="bg-white text-violet-700 hover:bg-zinc-100 font-medium px-8 py-6 text-base"
            >
              Explore Resources
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ResourceHubPage;
